import React, { ReactNode, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import ProgramsService from '../../services/programs.service';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import { BillingInterval, BillingPlanKey, PlanChangePreview, Program } from '../../models/billing.models';
import { formatCents, formatMoney, intervalLabel } from '../../helpers/billing.helpers';

export interface ProgramBillingActionsOptions {
  program: Program | null;
  onProgramChange: (program: Program) => void;
  /** Called when ending a free trial deactivates the program immediately. */
  onProgramEnded?: () => void;
}

export interface ProgramBillingActions {
  requestChangePlan: (planKey: BillingPlanKey, billingInterval: BillingInterval) => void;
  openCancelDialog: () => void;
  resumeCancellation: () => void;
  planChangeLoading: boolean;
  cancelLoading: boolean;
  /** Increments after each successful plan change; pass to ProgramBillingHistory to reload it. */
  historyVersion: number;
  /** Plan-change preview and cancel confirmation dialogs; render once. */
  dialogs: ReactNode;
}

/** Plan change (with Stripe proration preview), cancel, and resume for a program. */
const useProgramBillingActions = ({
  program,
  onProgramChange,
  onProgramEnded,
}: ProgramBillingActionsOptions): ProgramBillingActions => {
  const programsService = useService(ProgramsService);
  const { showSnackbar } = useSnackbarHelper();
  const [pendingChange, setPendingChange] = useState<{
    planKey: BillingPlanKey;
    billingInterval: BillingInterval;
  } | null>(null);
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [planChangeLoading, setPlanChangeLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const closeChange = () => {
    if (planChangeLoading) return;
    setPendingChange(null);
    setPreview(null);
  };

  const requestChangePlan = (planKey: BillingPlanKey, billingInterval: BillingInterval) => {
    if (!program) return;
    setPendingChange({ planKey, billingInterval });
    setPreview(null);
    setPreviewLoading(true);
    programsService
      .previewChangePlan(program.id, { planKey, billingInterval })
      .then((response) => setPreview(response.data.data))
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to preview plan change', 'error');
        setPendingChange(null);
      })
      .finally(() => setPreviewLoading(false));
  };

  const confirmChangePlan = () => {
    if (!program || !pendingChange) return;
    setPlanChangeLoading(true);
    programsService
      .changePlan(program.id, pendingChange)
      .then((response) => {
        onProgramChange(response.data.data);
        setHistoryVersion((version) => version + 1);
        showSnackbar('Plan updated', 'success');
        setPendingChange(null);
        setPreview(null);
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to change plan', 'error');
      })
      .finally(() => setPlanChangeLoading(false));
  };

  const cancelProgram = () => {
    if (!program) return;
    setCancelLoading(true);
    programsService
      .cancelProgram(program.id)
      .then((response) => {
        onProgramChange(response.data.data);
        setCancelDialogOpen(false);
        if (response.data.data.status === 'canceled') {
          showSnackbar('Free trial ended. Your teams were deactivated.', 'success');
          onProgramEnded?.();
        } else {
          showSnackbar('Cancellation scheduled — you keep access until the period ends', 'success');
        }
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to cancel subscription', 'error');
      })
      .finally(() => setCancelLoading(false));
  };

  const resumeCancellation = () => {
    if (!program) return;
    setCancelLoading(true);
    programsService
      .resumeCancellation(program.id)
      .then((response) => {
        onProgramChange(response.data.data);
        showSnackbar('Subscription will renew as usual', 'success');
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to resume subscription', 'error');
      })
      .finally(() => setCancelLoading(false));
  };

  const dialogs = (
    <>
      <Dialog open={Boolean(pendingChange)} onClose={closeChange} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm plan change</DialogTitle>
        <DialogContent>
          {previewLoading || !preview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Typography>
                From{' '}
                <strong>
                  {preview.currentPlanName} · {formatMoney(preview.currentDiscountedPrice)} /{' '}
                  {intervalLabel(preview.currentBillingInterval)}
                </strong>
                {preview.currentListPrice !== preview.currentDiscountedPrice ? (
                  <span style={{ color: '#5b6b82' }}> (list {formatMoney(preview.currentListPrice)})</span>
                ) : null}
              </Typography>
              {preview.renewalTeams && preview.renewalTeams.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography>
                    New {preview.targetBillingInterval === 'year' ? 'yearly' : 'monthly'} price for each team
                  </Typography>
                  {preview.renewalTeams.map((team) => (
                    <Typography key={team.teamId}>
                      <strong>
                        {team.teamName} · {team.planName} · {formatMoney(team.price)} /{' '}
                        {intervalLabel(team.billingInterval)}
                      </strong>
                      {team.chargedNow ? '' : ' · after the free trial'}
                      {team.moving ? '' : ' · already on this bill'}
                    </Typography>
                  ))}
                  <Typography>
                    {preview.targetBillingInterval === 'year' ? 'Yearly' : 'Monthly'} total{' '}
                    <strong>
                      {formatMoney(
                        preview.renewalTeams.reduce((sum, team) => sum + (team.chargedNow ? team.price || 0 : 0), 0)
                      )}{' '}
                      / {intervalLabel(preview.targetBillingInterval)}
                    </strong>
                  </Typography>
                </Box>
              ) : (
                <Typography>
                  To{' '}
                  <strong>
                    {preview.targetPlanName} · {formatMoney(preview.targetDiscountedPrice)} /{' '}
                    {intervalLabel(preview.targetBillingInterval)}
                  </strong>
                  {preview.targetListPrice !== preview.targetDiscountedPrice ? (
                    <span style={{ color: '#5b6b82' }}> (list {formatMoney(preview.targetListPrice)})</span>
                  ) : null}
                </Typography>
              )}
              {preview.intervalChange ? (
                <Typography color="warning.main">
                  The new billing cycle starts today. Every Small Team and Large Team moves with it.
                </Typography>
              ) : null}
              {preview.renewalTeams && preview.renewalTeams.length > 0 ? null : preview.alignedPlanNames &&
                preview.alignedPlanNames.length > 0 ? (
                <Typography>
                  This also moves {preview.alignedPlanNames.join(', ')} onto{' '}
                  {preview.targetBillingInterval === 'year' ? 'yearly' : 'monthly'} billing. Every team shares that one
                  bill.
                </Typography>
              ) : null}
              {preview.planChange && preview.targetMembersPerTeam !== preview.currentMembersPerTeam ? (
                <Typography>
                  {preview.targetMembersPerTeam > preview.currentMembersPerTeam
                    ? `Each team can grow from ${preview.currentMembersPerTeam} to ${preview.targetMembersPerTeam} members (coaches and athletes).`
                    : `Each team will be limited to ${preview.targetMembersPerTeam} members (coaches and athletes). Your teams already fit that limit.`}
                </Typography>
              ) : null}
              {preview.renewalTeams && preview.renewalTeams.length > 0 ? null : preview.extraTeams > 0 ? (
                <Typography>
                  {preview.extraTeams} extra {preview.extraTeams === 1 ? 'team stays' : 'teams stay'} on this
                  subscription
                  {preview.extraTeamPrice != null
                    ? ` at ${formatMoney(preview.extraTeamPrice)} / ${intervalLabel(
                        preview.targetBillingInterval
                      )} each.`
                    : '.'}
                </Typography>
              ) : null}
              {preview.immediateCharge ? (
                preview.intervalChange && preview.renewalTeams && preview.renewalTeams.length > 0 ? (
                  <Typography>
                    The charge today is the new price for each team that is moving, minus unused days on the current
                    bill.
                  </Typography>
                ) : (
                  <Typography>
                    Estimated charge now:{' '}
                    <strong>
                      {preview.canPreviewCharge
                        ? formatCents(preview.estimatedAmountDueCents)
                        : 'Stripe will calculate on confirm'}
                    </strong>
                  </Typography>
                )
              ) : (
                <Typography color="text.secondary">
                  You are still in the free trial — no card charge for this change.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeChange} disabled={planChangeLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmChangePlan}
            disabled={previewLoading || !preview || planChangeLoading}
          >
            {planChangeLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm change'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelLoading && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {program?.status === 'trialing'
            ? 'End free trial?'
            : program?.status === 'incomplete'
            ? 'Cancel pending program?'
            : program?.sharesBill
            ? 'Cancel this plan?'
            : 'Cancel subscription?'}
        </DialogTitle>
        <DialogContent>
          {program?.status === 'trialing' ? (
            <Typography sx={{ pt: 1 }}>
              This ends your free trial now. You will not be charged. Teams on this program are deactivated immediately.
              Each coach only gets one free trial.
            </Typography>
          ) : program?.status === 'incomplete' ? (
            <Typography sx={{ pt: 1 }}>
              This cancels your pending program. Pending teams will not appear in My Teams. You can start a catalog plan
              again later and subscribe to activate.
            </Typography>
          ) : (
            <Typography sx={{ pt: 1 }}>
              {program?.sharesBill
                ? `This plan drops off the bill${
                    program.currentPeriodEnd ? ` on ${dayjs(program.currentPeriodEnd).format('MMM D, YYYY')}` : ''
                  }. These teams stay available until then. Other plans on the bill keep renewing. There is no charge today.`
                : `Billing stops at the end of the current period${
                    program?.currentPeriodEnd ? ` (${dayjs(program.currentPeriodEnd).format('MMM D, YYYY')})` : ''
                  }. You keep access until then. There is no refund for unused time. When the period ends, program teams are deactivated.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
            {program?.sharesBill ? 'Keep this plan' : 'Keep subscription'}
          </Button>
          <Button color="error" variant="contained" onClick={cancelProgram} disabled={cancelLoading}>
            {cancelLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : program?.status === 'trialing' ? (
              'End free trial'
            ) : program?.status === 'incomplete' ? (
              'Cancel pending program'
            ) : (
              'Cancel at period end'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    requestChangePlan,
    openCancelDialog: () => setCancelDialogOpen(true),
    resumeCancellation,
    planChangeLoading,
    cancelLoading,
    historyVersion,
    dialogs,
  };
};

export default useProgramBillingActions;
